import React, { useContext, useEffect, useState } from "react";
import usePlayerUtils from "../../hooks/usePlayerUtils";
import "./RadioPlayerMain.css";
import ProgressBar from "../ProgressBar/ProgressBar";
import { useAudioVisualizer } from "../../hooks/useAudioVisualizer";
import VolumeBar from "./VolumeBar";
import { AuthContext } from "../../services/AuthContext";
import usePlayerControls from "../../hooks/usePlayerControls";

export default function RadioPlayerHeader() {
  const {
    audioRef,
    currentTrack,
    nowPlaying,
    playbackState,
    setPlaybackState,
    detachHls,
    setCurrentTimeSec,
    playbackTimeMs,
    currentLiveMs,
    bufferStartMs,
    progressBarPercent,
    durationPercent,
    formatTimestamp,
    setManualTrack,
    setIsSeeking,
    parseDuration,
    setTimeShiftOffsetSec,
    timeShiftOffsetSec,
  } = usePlayerUtils();

  const {
    wrapChildClick,
    togglePlay,
  } = usePlayerControls({});

  const artist = currentTrack?.artist;
  const songTitle = currentTrack?.title;

  const { isLoggedIn, setUserModalOpen } = useContext(AuthContext);
  const canvasRef = useAudioVisualizer(audioRef);
  const [volume, setVolume] = useState(1);

  useEffect(() => {
    const audio = audioRef.current;
    if (!audio) return;

    const onTime = () => setCurrentTimeSec(audio.currentTime || 0);
    audio.addEventListener("timeupdate", onTime);
    audio.addEventListener("seeked", onTime);
    audio.addEventListener("loadedmetadata", onTime);
    return () => {
      audio.removeEventListener("timeupdate", onTime);
      audio.removeEventListener("seeked", onTime);
      audio.removeEventListener("loadedmetadata", onTime);
    };
  }, []);

  const stopPlayback = () => {
    const audio = audioRef.current;
    if (!audio) return;

    audio.pause();
    detachHls();
    setPlaybackState("stopped");
    setCurrentTimeSec(0);
    setTimeShiftOffsetSec(0);
  };

  const handleLiveBtn = (e) => {
    const audio = audioRef.current;
    if (!audio) return;

    handleResumeLive();
    setTimeShiftOffsetSec(0);

    if (playbackState !== "playing") {
      audio.play();
      setPlaybackState("playing");
    }

    audio.currentTime = timeShiftOffsetSec + audio.currentTime;
    setCurrentTimeSec(timeShiftOffsetSec + audio.currentTime);
  }

  const handleResumeLive = () => {
    setIsSeeking(false);
    setManualTrack(null);
  };

  const performSeek = (percent) => {
    // if (!isLoggedIn) {
    //   setUserModalOpen(true);
    //   return;
    // }

    const { maxTimeShiftSeconds, currentLiveTime, history } = nowPlaying || {};

    const audio = audioRef.current;
    if (!audio || !maxTimeShiftSeconds || !currentLiveTime || !history?.length) return;

    const currentLiveMs = new Date(currentLiveTime).getTime();
    const bufferStartMs = currentLiveMs - maxTimeShiftSeconds * 1000;
    const bufferEndMs = currentLiveMs;
    const desiredMs = bufferStartMs + percent * (bufferEndMs - bufferStartMs);

    const seekSec = (desiredMs - bufferStartMs) / 1000;
    const realCurrentTime = timeShiftOffsetSec + audio.currentTime;
    const currentSeekSec = realCurrentTime - maxTimeShiftSeconds + seekSec;

    const newOffset = realCurrentTime - currentSeekSec;
    setTimeShiftOffsetSec(newOffset);

    audio.currentTime = currentSeekSec;
    setCurrentTimeSec(currentSeekSec);

    const seekTimestamp = bufferStartMs + currentSeekSec * 1000;

    const currentSong = history.find(song => {
      const start = new Date(song.startedAt).getTime();
      const end = start + parseDuration(song.duration) * 1000;
      return seekTimestamp >= start && seekTimestamp < end;
    });

    if (currentSong) {
      setManualTrack(currentSong || null);
      setIsSeeking(true);
    }
  };

  const playerProgressInfo = () => {
    const liveBtnClassName = timeShiftOffsetSec <= 0 || playbackState === "stopped" ? "live-btn-disabled live-button" : "live-button";

    return (
      <div className="d-status-progerss">
        <button className={liveBtnClassName} onClick={wrapChildClick(() => handleLiveBtn())} disabled={timeShiftOffsetSec <= 0 || playbackState === "stopped"} >
          <div className="live-text">GO LIVE</div>
        </button>
        <div className="colapsed-times">
          <div className="d-time">{bufferStartMs ? formatTimestamp(bufferStartMs) : "--:--:--"}</div>
          <div className="d-time">{currentLiveMs ? formatTimestamp(currentLiveMs) : "--:--:--"}</div>
        </div>
        <div className="colapsed-progress">
          <ProgressBar progressPercent={progressBarPercent / 100} onSeek={performSeek} />
        </div>
        <div className="d-time d-playbeck-time">
          <div>
            {playbackTimeMs ? new Date(playbackTimeMs).toLocaleTimeString("en-GB", { hour12: false }) : "--:--:--"}
          </div>
        </div>
      </div>
    )
  }

  const playerControll = () => {
    const stopBtnClassName = playbackState === "stopped" ? "btn-disabled stop-btn" : "stop-btn";
    const cnavasClassName = playbackState === "stopped" ? "d-stopped-analizer" : "d-analyzer"

    return (
      <div className="d-cover-info-btn">
        <div className="colapsed-btn">
          <div className="colapsed-player-btn">
            <div className="progress-ring" style={{ "--percent": `${durationPercent() * 100}%` }}>
              <button className="play-btn" onClick={wrapChildClick(() => togglePlay())}>
                {playbackState === "playing"
                  ? <i className="fas fa-pause"></i>
                  : <i className="fas fa-play"></i>
                }
              </button>
            </div>
          </div>
        </div>
        <div className="colapsed-info">
          <div className="d-song-info">
            <div className="d-colapsed-song-info">
              <div className="d-song-title">{songTitle}</div>
              <div className="d-artist">{artist}</div>
            </div>
          </div>
          <div className={cnavasClassName}>
            <canvas
              ref={canvasRef}
              width={200}
              height={80}
              className="d-colapsed-analyzer"
            />
          </div>


        </div>
        <div className="colapsed-btn">
          <div className="stop-btn-ring" style={{ "--percent": "100%" }}>
            <button className={stopBtnClassName} onClick={wrapChildClick(() => stopPlayback())} disabled={playbackState === "stopped"}>
              <i className="fas fa-stop"></i>
            </button>
          </div>
        </div>
      </div>
    )
  }

  const volumeBar = () => {
    return (
      <div className="volume-section">
        <VolumeBar value={volume} onChange={(v) => { const audio = audioRef.current; if (audio) { audio.volume = v; } setVolume(v); }} />
        <i className={`fas ${volume < 0.01 ? 'fa-volume-mute' : volume < 0.5 ? 'fa-volume-down' : 'fa-volume-up'}`}></i>
      </div>
    );
  };

  return (
    <div className="radio-player-main">
      <div className="player-main">
        {playerControll()}
        {playerProgressInfo()}
        {volumeBar()}
      </div>
    </div>
  );
}
